import { memo, useMemo, useCallback } from 'react';
import { getDaysInMonth, isToday } from '../../../hooks/useDateRangePicker';

interface CalendarGridProps {
  currentMonth: Date;
  isSelected: (date: Date) => boolean;
  isInRange: (date: Date) => boolean;
  isRangeStart: (date: Date) => boolean;
  isRangeEnd: (date: Date) => boolean;
  onDayClick: (date: Date) => void;
}

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

/**
 * Get CSS classes for a calendar day
 */
const getDayClasses = (
  date: Date | null,
  isSelected: (date: Date) => boolean,
  isInRange: (date: Date) => boolean,
  isRangeStart: (date: Date) => boolean,
  isRangeEnd: (date: Date) => boolean,
): string => {
  if (!date) {
    return 'calendar-day empty';
  }

  const classes = ['calendar-day'];

  isToday(date) && classes.push('today');
  isSelected(date) && classes.push('selected');
  isInRange(date) && classes.push('in-range');
  isRangeStart(date) && classes.push('range-start');
  isRangeEnd(date) && classes.push('range-end');

  return classes.join(' ');
};

/**
 * Calendar grid component for displaying days of the month
 */
export const CalendarGrid = memo(function CalendarGrid({
  currentMonth,
  isSelected,
  isInRange,
  isRangeStart,
  isRangeEnd,
  onDayClick,
}: CalendarGridProps) {
  const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);

  const handleDayClick = useCallback(
    (date: Date | null) => {
      date && onDayClick(date);
    },
    [onDayClick],
  );

  return (
    <>
      <div className="calendar-weekdays">
        {WEEKDAYS.map((day) => (
          <span key={day} className="calendar-weekday">
            {day}
          </span>
        ))}
      </div>

      <div className="calendar-days">
        {days.map((date, index) => (
          <button
            key={index}
            type="button"
            className={getDayClasses(date, isSelected, isInRange, isRangeStart, isRangeEnd)}
            onClick={() => handleDayClick(date)}
            disabled={!date}
            aria-label={date ? date.toLocaleDateString() : undefined}
          >
            {date?.getDate()}
          </button>
        ))}
      </div>
    </>
  );
});
